<template>
  <section class="">
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="paddingTop === 'reduced' ? 'pt-0 pb-8 lg:pt-0 lg:pb-12' : 'py-12 lg:py-16'"
    >
      <div class="">
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-6">
          <article
            v-for="(column, index) in columns"
            :key="index"
            class="flex flex-col gap-6 lg:gap-[25px]"
          >
            <div class="w-full h-[190px]">
              <nuxt-img
                v-if="column.imageSrc"
                :src="column.imageSrc"
                :alt="column.imageAlt"
                width="358"
                height="190"
                format="webp"
                loading="lazy"
                sizes="xs:100vw sm:358px lg:358px"
                class="object-cover w-full h-[190px] rounded-2xl"
              />
              <div
                v-else
                class="w-full h-[190px] rounded-2xl bg-[#2A2A2A]"
                aria-hidden="true"
              />
            </div>

            <div class="flex flex-col gap-4">
              <div
                v-if="column.badgeText"
                class="inline-flex items-start self-start px-3.5 py-1 rounded border border-[#E0D5C5] bg-[#F0E8D8]"
              >
                <span
                  class="text-xs tracking-wide text-[#6B3A22] uppercase font-ww-regular"
                  v-html="column.badgeText"
                />
              </div>

              <h3
                v-if="column.title"
                class="text-22 font-bold leading-27 font-ww-bold text-[#4A4A4A]"
                v-html="column.title"
              />

              <div
                v-if="column.content"
                class="text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:space-y-0 [&_strong]:font-ww-bold [&_sup]:text-xs"
                v-html="column.content"
              />
            </div>
          </article>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
defineProps({
  columns: {
    type: Array,
    default: () => [
      {
        imageSrc: '/assets/images/supplement-focus-probiotic-mens.jpg',
        imageAlt: 'Microscopic view of probiotic bacteria',
        badgeText: 'Supplement Focus',
        title: 'A Good Probiotic',
        content: `<p>As you age, cellular health declines, and this affects the health of your gut. Because of this, your immune system (housed in your gut) doesn't function as efficiently which can lead to increased inflammation throughout the body, improper nutrient absorption, and therefore an overall increased risk of disease.<sup>20</sup></p>
<p>Supplementing with a quality probiotic supports a healthy balance of bacteria within your gut, and this balance doesn't just aid in proper digestion but it can bring support (and balance) throughout your body.</p>`
      },
      {
        imageSrc: '/assets/images/supplement-focus-sodium-butyrate-mens.jpg',
        imageAlt: 'Sodium butyrate powder in a bowl',
        badgeText: 'Supplement Focus',
        title: 'Sodium Butyrate',
        content: `<p>Sodium Butyrate is a short chain fatty acid produced in the colon when gut bacteria ferment dietary fiber.</p>
<p>Studies have shown supplementing with sodium butyrate can aid your gut health by reducing inflammation which may reduce your risk of disease, supporting overall digestive health, and providing energy for the cells in your colon.</p>
<p>Sodium butyrate has been shown to aid in:</p>
<ul>
  <li>IBS management</li>
  <li>Improving general digestive discomfort</li>
  <li>Repairing the gut lining (leaky gut)</li>
  <li>Supporting overall metabolic health<sup>21</sup></li>
</ul>`
      }
    ]
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

defineBuilderComponent({
  friendlyName: 'Content - Supplement Focus Columns',
  inputs: [
    {
      name: 'columns',
      type: 'list',
      defaultValue: [
        {
          imageSrc: '/assets/images/supplement-focus-probiotic-mens.jpg',
          imageAlt: 'Microscopic view of probiotic bacteria',
          badgeText: 'Supplement Focus',
          title: 'A Good Probiotic',
          content: `<p>As you age, cellular health declines, and this affects the health of your gut. Because of this, your immune system (housed in your gut) doesn't function as efficiently which can lead to increased inflammation throughout the body, improper nutrient absorption, and therefore an overall increased risk of disease.<sup>20</sup></p>
<p>Supplementing with a quality probiotic supports a healthy balance of bacteria within your gut, and this balance doesn't just aid in proper digestion but it can bring support (and balance) throughout your body.</p>`
        },
        {
          imageSrc: '/assets/images/supplement-focus-sodium-butyrate-mens.jpg',
          imageAlt: 'Sodium butyrate powder in a bowl',
          badgeText: 'Supplement Focus',
          title: 'Sodium Butyrate',
          content: `<p>Sodium Butyrate is a short chain fatty acid produced in the colon when gut bacteria ferment dietary fiber.</p>
<p>Studies have shown supplementing with sodium butyrate can aid your gut health by reducing inflammation which may reduce your risk of disease, supporting overall digestive health, and providing energy for the cells in your colon.</p>
<p>Sodium butyrate has been shown to aid in:</p>
<ul>
  <li>IBS management</li>
  <li>Improving general digestive discomfort</li>
  <li>Repairing the gut lining (leaky gut)</li>
  <li>Supporting overall metabolic health<sup>21</sup></li>
</ul>`
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'badgeText',
          type: 'text',
          defaultValue: 'Supplement Focus'
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'content',
          type: 'richText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
